import { useConnection } from "@solana/wallet-adapter-react";
import type { FranciumPositions } from "@stepfinance/lending";
import { FranciumLendingPositionFetcher } from "@stepfinance/lending";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { useLiquidity } from "../../../contexts/liquidity";
import { useMarkets } from "../../../contexts/market";
import { getTokenByMint } from "../../../utils/tokenInfo";
import { useConnectedWallet } from "../../wallet";
import { useLiquidityPoolsQuery } from "../liquidity/useLiquidityPoolsQuery";

export const DEFAULT_FRANCIUM_POSITIONS: FranciumPositions = {
  collateral: {},
  yieldFarms: {},
};

export const useFranciumQuery = () => {
  const { connection } = useConnection();
  const wallet = useConnectedWallet();
  const { priceInUSD, marketsInitialised } = useMarkets();
  const { raydiumPools, orcaPools } = useLiquidity();
  const { isSuccess } = useLiquidityPoolsQuery();

  const fetcher = useMemo(() => {
    if (wallet) {
      return new FranciumLendingPositionFetcher({
        connection,
        wallet,
        underlyingPoolsData: { ...raydiumPools, ...orcaPools },
        getTokenByMint,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connection, isSuccess, wallet]);

  return useQuery<FranciumPositions>(
    ["franciumPositions", fetcher?.wallet.publicKey.toBase58()],
    async () => {
      if (fetcher) {
        return await fetcher.fetchPositions(priceInUSD);
      }
      return DEFAULT_FRANCIUM_POSITIONS;
    },
    {
      retry: false,
      enabled: !!fetcher && marketsInitialised && !!priceInUSD && isSuccess,
    },
  );
};
